import React, { useState, useEffect } from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { Row, Col } from "react-bootstrap";
import FormControlLabel from "@mui/material/FormControlLabel";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import makeStyles from "@mui/styles/makeStyles";
import Slider from "@mui/material/Slider";
import Box from "@mui/material/Box";
import {
  ArrowDownward,
  ChevronLeft,
  ChevronRight,
  Clear,
  FilterList,
  FirstPage,
  LastPage,
  Search,
  ViewColumn,
} from "@mui/icons-material";

const tableIcons = {
  Clear: React.forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  DetailPanel: React.forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Filter: React.forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: React.forwardRef((props, ref) => (
    <FirstPage {...props} ref={ref} />
  )),
  LastPage: React.forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: React.forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  PreviousPage: React.forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: React.forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: React.forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: React.forwardRef((props, ref) => (
    <ArrowDownward {...props} ref={ref} />
  )),
  ViewColumn: React.forwardRef((props, ref) => (
    <ViewColumn {...props} ref={ref} />
  )),
};

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  pagination: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  switch: {
    paddingLeft: "1.5em",
  },
}));

function ResumenActivos() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [activo, setActivo] = useState([]);
  const [ultimaModificacion, setUltimaModificacion] = useState(null);
  const [filter, setFilter] = useState(false);

  // rango para filtrar por "percentil"
  const [range, setRange] = useState([0, 100]);

  const handleRangeChange = (event, newValue) => {
    setRange(newValue);
  };

  // filtrar datos por el campo "percentil"
  const filteredData = activo.filter((row) => {
    if (row.percentil === undefined || row.percentil === null) return true;
    return row.percentil >= range[0] && row.percentil <= range[1];
  });

  // formatear números
  const formatNumber = (value) => {
    const number = parseFloat(value);
    if (isNaN(number)) return value;
    const formatted = number.toFixed(2);
    const [integerPart, decimalPart] = formatted.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${formattedInteger}.${decimalPart}`;
  };

  const getClasificacionBgColor = (clasificacion) => {
    if (!clasificacion) return "transparent";
    const lower = clasificacion.toLowerCase();
    if (
      lower.includes("critico") ||
      lower.includes("muy critico") ||
      lower.includes("crítico") ||
      lower.includes("muy crítico") ||
      lower.includes("confidencial") ||
      lower.includes("restringida")
    )
      return "red";
    if (
      lower.includes("moderado") ||
      lower.includes("medio") ||
      lower.includes("interna")
    )
      return "orange";
    if (
      lower.includes("tolerable") ||
      lower.includes("bajo") ||
      lower.includes("publica")
    )
      return "#81C784";
    return "transparent";
  };

  const columns = [
    { field: "id_ACI", title: "Id Activo", align: "justify" },
    { field: "ranking", title: "Ranking", align: "justify" },
    {
      field: "nombre",
      title: "Nombre",
      cellStyle: { minWidth: "250px", width: "25%" },
      headerStyle: { minWidth: "250px", width: "25%" },
    },
    {
      field: "contenedores_evaluados",
      title: "Contenedores Evaluados",
      render: (rowData) => {
        if (!rowData.contenedores_evaluados) return "-";
        const match = rowData.contenedores_evaluados.match(/\(([\d.]+)%\)/);
        const porcentaje = match ? parseFloat(match[1]) : 0;

        let style = {
          padding: "4px 8px",
          borderRadius: "6px",
          fontWeight: "bold",
          color: "#fff",
          backgroundColor: "#f44336",
        };

        if (porcentaje >= 90) {
          style.backgroundColor = "#81C784";
          style.color = "#1B5E20";
        } else if (porcentaje >= 60) {
          style.backgroundColor = "#FFB74D";
          style.color = "#4E342E";
        }

        return <span style={style}>{rowData.contenedores_evaluados}</span>;
      },
    },
    {
      field: "cantidad_efectos",
      title: "Cantidad Efectos",
      render: (rowData) => (
        <div style={{ fontWeight: "bold", marginBottom: "2px" }}>
          {rowData.cantidad_efectos}
        </div>
      ),
    },
    {
      field: "general_P95",
      title: "Total(P95)",
      render: (rowData) => (
        <div style={{ fontWeight: "bold", marginBottom: "2px" }}>
          {formatNumber(rowData.general_P95)}
        </div>
      ),
    },
    {
      field: "riesgo_Confidencialidad_P95",
      title: "Confidencialidad\n(P95)",
      render: (rowData) => (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: "bold", marginBottom: "2px" }}>
            {formatNumber(rowData.riesgo_Confidencialidad_P95)}
          </div>
          <div
            style={{
              fontSize: "0.85em",
              fontWeight: "bold",
              padding: "2px 6px",
              borderRadius: "4px",
              display: "inline-block",
              backgroundColor: getClasificacionBgColor(rowData.clasificacion_c),
              color:
                getClasificacionBgColor(rowData.clasificacion_c) !==
                "transparent"
                  ? "white"
                  : "#666",
            }}
          >
            {rowData.clasificacion_c || "-"}
          </div>
        </div>
      ),
    },
    {
      field: "riesgo_Integridad_P95",
      title: "Integridad\n(P95)",
      render: (rowData) => (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: "bold", marginBottom: "2px" }}>
            {formatNumber(rowData.riesgo_Integridad_P95)}
          </div>
          <div
            style={{
              fontSize: "0.85em",
              fontWeight: "bold",
              padding: "2px 6px",
              borderRadius: "4px",
              display: "inline-block",
              backgroundColor: getClasificacionBgColor(rowData.clasificacion_i),
              color:
                getClasificacionBgColor(rowData.clasificacion_i) !==
                "transparent"
                  ? "white"
                  : "#666",
            }}
          >
            {rowData.clasificacion_i || "-"}
          </div>
        </div>
      ),
    },
    {
      field: "riesgo_Disponibilidad_P95",
      title: "Disponibilidad\n(P95)",
      render: (rowData) => (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: "bold", marginBottom: "2px" }}>
            {rowData.riesgo_Disponibilidad_P95
              ? formatNumber(rowData.riesgo_Disponibilidad_P95)
              : "-"}
          </div>
          <div
            style={{
              fontSize: "0.85em",
              fontWeight: "bold",
              padding: "2px 6px",
              borderRadius: "4px",
              display: "inline-block",
              backgroundColor: getClasificacionBgColor(rowData.clasificacion_d),
              color:
                getClasificacionBgColor(rowData.clasificacion_d) !==
                "transparent"
                  ? "white"
                  : "#666",
            }}
          >
            {rowData.clasificacion_d || "-"}
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result = await fetch(
        `${process.env.REACT_APP_API_URL}evaluacion/resumenACI/${localStorage.getItem(
          "idcompania"
        )}/`,
        {
          method: "GET",
          headers: { Accept: "application/json" },
        }
      );
      if (result.ok) {
        const data = await result.json();
        setActivo(data["data"]);
        setUltimaModificacion(data["fecha_modificacion"]);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleChange = () => {
    setFilter(!filter);
  };

  const theme = createMuiTheme({
    palette: { primary: { main: "#2c2a29" }, secondary: { main: "#ff9100" } },
    overrides: {
      MuiTableHead: {
        root: {
          "& .MuiTableRow-root": { borderBottom: "none !important" },
        },
      },
      MuiTableCell: {
        head: { borderBottom: "none !important", borderTop: "none !important" },
      },
    },
  });

  return (
    <Paper variant="outlined" className={classes.paper}>
      {/* Slider arriba */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          px: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
          Percentil
        </Typography>
        <Slider
          size="small"
          value={range}
          onChange={handleRangeChange}
          valueLabelDisplay="auto"
          min={0}
          max={100}
          sx={{
            width: 200,
            height: 6,
            color: "transparent",
            "& .MuiSlider-track": {
              border: "none",
              background: "linear-gradient(to right, #ffcccc, #cc0000)",
            },
            "& .MuiSlider-rail": {
              opacity: 1,
              background: "linear-gradient(to right, #ffcccc, #cc0000)",
            },
            "& .MuiSlider-thumb": {
              width: 14,
              height: 14,
              backgroundColor: "#fff",
              border: "2px solid #cc0000",
            },
          }}
        />
      </Box>

      <MuiThemeProvider theme={theme}>
        <MaterialTable
          columns={columns}
          data={filteredData}
          icons={tableIcons}
          options={{
            showTitle: false,
            filtering: filter,
            searchFieldAlignment: "left",
            searchFieldStyle: { height: 50 },
            headerStyle: {
              backgroundColor: "#2c2a29",
              color: "#ffffff",
              position: "sticky",
              top: 0,
              fontFamily: "CIBFont Sans Regular",
              fontWeight: 400,
              fontSize: "1.2rem",
              lineHeight: 1.6,
              letterSpacing: "0.0075em",
              flex: "1 1 100%",
              borderBottom: "none",
              borderTop: "none",
            },
            maxBodyHeight: "650px",
            selection: false,
            disableMultipleSelection: true,
            pageSizeOptions: [10, 20, 30],
            showSelectAllCheckbox: false,
            showTextRowsSelected: false,
            pageSize: 10,
          }}
          components={{
            Toolbar: (props) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, px: 2 }}>
                {/* Barra de búsqueda */}
                <Box sx={{ flex: 1 }}>
                  <MTableToolbar {...props} />
                </Box>
                {/* Mostrar fecha con título */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Ultima Modificacion:
                  </Typography>
                  <Typography variant="body2">{ultimaModificacion}</Typography>
                </Box>
              </Box>
            ),
            Pagination: (props) => {
              const {
                page,
                count,
                rowsPerPage,
                onChangePage,
                onChangeRowsPerPage,
              } = props;

              return (
                <div className={classes.pagination}>
                  <FormControlLabel
                    className={classes.switch}
                    control={
                      <Checkbox
                        checked={filter}
                        onChange={handleChange}
                        inputProps={{ "aria-label": "primary checkbox" }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Mostrar filtros por columnas
                      </Typography>
                    }
                  />
                  <TablePagination
                    component="div"
                    count={count || 0}
                    page={page || 0}
                    rowsPerPage={rowsPerPage || 10}
                    rowsPerPageOptions={[10, 20, 30]}
                    onPageChange={(event, newPage) => {
                      if (onChangePage) onChangePage(event, newPage);
                    }}
                    onRowsPerPageChange={(event) => {
                      if (onChangeRowsPerPage) onChangeRowsPerPage(event);
                    }}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} de ${
                        count !== -1 ? count : `más de ${to}`
                      }`
                    }
                  />
                </div>
              );
            },
          }}
          localization={{
            toolbar: { searchPlaceholder: "Buscar" },
            body: { emptyDataSourceMessage: "No se encontraron registros" },
            pagination: {
              labelRowsSelect: "Filas",
              firstTooltip: "Primera página",
              previousTooltip: "Anterior",
              nextTooltip: "Siguiente",
              lastTooltip: "Última página",
            },
          }}
        />
      </MuiThemeProvider>
    </Paper>
  );
}

export default ResumenActivos;
